import * as React from 'react';
import { useCallback, useState } from 'react';

import { translate } from '@showbie-socrative/socrative-utils/lib/translator/client';

import { Dialog } from '~shared/components/Dialog';
import {
  DialogProps,
  useDialog,
  useOpenDialogAnimation,
} from '~shared/context/AsyncDialogContext';
import { SpecificStudent } from '~teacher/components/ResultsGrid/Insights/GenerateQuestions/SpecificStudents/SpecificStudent';
import { HideIntercomLauncher } from '~teacher/hook/utils/HideIntercomLauncher';

import { AreasOfImprovement } from './AreasOfImprovement/AreasOfImprovement';
import { SelectGenerationType } from './SelectGenerationType';

export type GenerationType =
  | 'select-generation-type'
  | 'areas-of-improvement'
  | 'specific-students';

interface GenerateQuestionsDialogProps {
  activityInstanceId: number;
}

export function GenerateQuestionsDialog({
  activityInstanceId,
  onResolve,
}: GenerateQuestionsDialogProps & DialogProps): JSX.Element {
  const [generationType, setGenerationType] = useState<GenerationType>(
    'areas-of-improvement'
  );

  const backToGenerationType = () =>
    setGenerationType('select-generation-type');

  const isOpen = useOpenDialogAnimation();

  return (
    <>
      <Dialog
        position="right"
        showBackdrop={false}
        open={isOpen}
        onClose={() => onResolve({ success: false })}
        id="generate-questions-dialog"
        size="medium"
        title={translate('Generate Questions')}
        closeLabel={translate('Cancel')}
      >
        {() => {
          switch (generationType) {
            case 'select-generation-type':
              return (
                <SelectGenerationType setGenerationType={setGenerationType} />
              );
            case 'areas-of-improvement':
              return (
                <AreasOfImprovement activityInstanceId={activityInstanceId} />
              );
            case 'specific-students':
              return (
                <SpecificStudent backToGenerationType={backToGenerationType} />
              );
            default:
              return <div>Not Done yet.</div>;
          }
        }}
      </Dialog>
      <HideIntercomLauncher />
    </>
  );
}

export function useOpenGenerateQuestionsDialog() {
  const { openDialog } = useDialog();

  return useCallback(
    async (options: GenerateQuestionsDialogProps): Promise<boolean> => {
      const result = await openDialog(GenerateQuestionsDialog, options);
      return result.success;
    },
    [openDialog]
  );
}
